import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, SearchLine, WorkstreamsLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {EmployeeRoleLevel, WorkstreamBasic, WorkstreamPrivacy} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';

import {useWorkstreamCreate, useWorkstreamTypeaheadSearch} from './hooks';
import {sortByTimeframeEligibility} from './subworkstreamTimeframe';
import {getWorkstreamDri, isWorkstreamStatusArchived} from './utils';

type CreateWorkstreamResult = {
  type: 'create-workstream';
  inputValue: string;
};

type WorkstreamTypeaheadResult = WorkstreamBasic | CreateWorkstreamResult;

type WorkstreamTypeaheadProps = {
  selectedIds: number[];
  placeholder?: string;
  valueFallback?: string;
  onSelect: (workstream: WorkstreamBasic) => void;
  inputIcon?: React.ReactNode;
  autoFocus?: boolean;
  disabled?: boolean;
  menuStyle?: React.CSSProperties;
  archiveWorkstreams?: boolean;
  driOverride?: EmployeeRoleLevel;
  disableCreateWorkstream?: boolean;
  excludeArchivedWorkstreams?: boolean;
  includeTrackers?: boolean;
  // Why an option can't be picked (it's listed last, disabled, with this as its subtitle).
  getDisabledReason?: (workstream: WorkstreamBasic) => string | undefined;
};

const getAvailableWorkstreamOptions = ({
  workstreams,
  selectedIds,
  excludeArchivedWorkstreams,
  includeTrackers,
}: {
  workstreams?: WorkstreamBasic[];
  selectedIds: number[];
  excludeArchivedWorkstreams?: boolean;
  includeTrackers?: boolean;
}) =>
  workstreams?.filter(
    (workstream) =>
      !selectedIds.includes(workstream.id) &&
      (includeTrackers || workstream.status_cadence !== 'none') &&
      (!excludeArchivedWorkstreams ||
        (!workstream.archived && !isWorkstreamStatusArchived(workstream)))
  );

export const WorkstreamTypeahead = (props: WorkstreamTypeaheadProps) => {
  return (
    <WorkstreamTypeaheadComponent
      workstreamOptions={undefined}
      isLoading={false}
      remoteSearch
      {...props}
    />
  );
};

export const WorkstreamTypeaheadComponent = ({
  workstreamOptions,
  isLoading,
  autoFocus,
  placeholder,
  valueFallback,
  onSelect,
  inputIcon,
  disabled,
  menuStyle,
  archiveWorkstreams,
  driOverride,
  disableCreateWorkstream,
  excludeArchivedWorkstreams,
  includeTrackers,
  selectedIds,
  getDisabledReason,
  remoteSearch = false,
}: WorkstreamTypeaheadProps & {
  workstreamOptions: WorkstreamBasic[] | undefined;
  isLoading: boolean;
  remoteSearch?: boolean;
}) => {
  const {createWorkstream, isPending} = useWorkstreamCreate({archive: archiveWorkstreams});
  const {employee: loggedInEmployee} = useAtomValue(loggedInEmployeeAtom);

  const [inputValue, setInputValue] = useState('');
  const {data: remoteOptions, isLoading: isRemoteLoading} = useWorkstreamTypeaheadSearch({
    input: inputValue,
    enabled: remoteSearch,
  });
  const [filteredOptions, setFilteredOptions] = useState<WorkstreamBasic[]>();
  const localWorkstreamOptions = useMemo(
    () =>
      getAvailableWorkstreamOptions({
        workstreams: workstreamOptions,
        selectedIds,
        excludeArchivedWorkstreams,
        includeTrackers,
      }),
    [excludeArchivedWorkstreams, includeTrackers, selectedIds, workstreamOptions]
  );
  const remoteWorkstreamOptions = useMemo(
    () =>
      getAvailableWorkstreamOptions({
        workstreams: remoteOptions,
        selectedIds,
        excludeArchivedWorkstreams,
        includeTrackers,
      }),
    [excludeArchivedWorkstreams, includeTrackers, remoteOptions, selectedIds]
  );
  const availableWorkstreamOptions = remoteSearch
    ? remoteWorkstreamOptions
    : localWorkstreamOptions;

  useEffect(() => {
    if (remoteSearch) {
      return;
    }

    if (!isLoading && availableWorkstreamOptions?.length) {
      setFilteredOptions(
        [...availableWorkstreamOptions]
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
          .slice(0, 10)
      );
      return;
    }

    if (!isLoading) {
      setFilteredOptions([]);
    }
  }, [availableWorkstreamOptions, isLoading, remoteSearch]);

  const inputContainerRef = useRef<HTMLElement | null>(null);
  const isSearching = remoteSearch ? isRemoteLoading : isLoading;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
  const undisabledOptions = remoteSearch
    ? (availableWorkstreamOptions ?? [])
    : (filteredOptions ?? []);
  const displayedOptions = getDisabledReason
    ? sortByTimeframeEligibility(undisabledOptions, getDisabledReason)
    : undisabledOptions;
  const results = useMemo<WorkstreamTypeaheadResult[]>(() => {
    const nextFilteredOptions = displayedOptions;
    if (!inputValue.length || disableCreateWorkstream) {
      return nextFilteredOptions;
    }

    return [{type: 'create-workstream', inputValue}, ...nextFilteredOptions];
  }, [disableCreateWorkstream, displayedOptions, inputValue]);
  const resultsSignature = useMemo(
    () =>
      `${isSearching ? 'loading' : 'ready'}:${inputValue}:${results
        .map((result) => ('type' in result ? `create:${result.inputValue}` : `ws:${result.id}`))
        .join(',')}`,
    [inputValue, isSearching, results]
  );
  const initialResults = useMemo(() => {
    if (results.length === 0) {
      return 0;
    }

    const defaultVisibleResults = inputValue.length && !disableCreateWorkstream ? 6 : 5;
    return Math.min(results.length, defaultVisibleResults);
  }, [disableCreateWorkstream, inputValue.length, results.length]);

  const handleSelection = (value: number) => {
    setInputValue('');
    const workstream = availableWorkstreamOptions?.find((team) => team.id === value);
    if (workstream && !getDisabledReason?.(workstream)) {
      onSelect(workstream);
    }
  };

  const renderTypeaheadRow = (result?: WorkstreamTypeaheadResult) => {
    if (!result || 'type' in result) {
      return renderNoResultsRow(result?.inputValue ?? inputValue);
    }

    const owner = getWorkstreamDri(result);
    const disabledReason = getDisabledReason?.(result);
    const subtitle =
      disabledReason ??
      [
        owner?.name,
        result.archived || isWorkstreamStatusArchived(result) ? t('archived') : undefined,
      ]
        .filter(Boolean)
        .join(' · ');

    return (
      <Typeahead.Row
        key={result.id}
        value={result.id}
        disabled={Boolean(disabledReason)}
        withLeftAccessory={<UIIcon src={WorkstreamsLine} />}
        withRightAccessory={owner && <Avatar user={owner} size="small" />}
        withSubtitle={subtitle}
        withTitle={<Text style={{wordBreak: 'break-word'}}>{result.name}</Text>}
        role="menuitemcheckbox"
        aria-label={`Option ${result.name}`}
      />
    );
  };

  const renderNoResultsRow = (createValue: string) => {
    if (disableCreateWorkstream) {
      return null;
    }

    return (
      <Typeahead.Row
        disabled={isPending}
        onClick={async () => {
          const workstream = {
            name: createValue,
            slug: null,
            description: '',
            start_date: new Date().toISOString(),
            end_date: null,
            status_cadence: 'semimonthly',
            status_due: 1,
            links: [],
            metrics: [],
            dependencies: [],
            employee_associations: driOverride
              ? [{user_id: driOverride.user_id, dri: 'dri'}]
              : [
                  {
                    user_id: loggedInEmployee.user_id,
                    dri: 'dri',
                  },
                ],
            privacy: WorkstreamPrivacy.PUBLIC,
            private_employees: [],
            private_teams: [],
          };

          const workstreamId = await createWorkstream({
            data: workstream,
          });

          if (workstreamId) {
            setInputValue('');
            onSelect({
              id: workstreamId,
              ...workstream,
              dependencies_count: workstream.dependencies.length,
              employee_associations: [
                {
                  employee: loggedInEmployee,
                  dri: 'dri',
                },
              ],
            });
          }
        }}
        withLeftAccessory={isPending ? <Spinner /> : <UIIcon src={AddLine} />}
        withTitle={<Text isBold>{t('add_workstream_name', {name: createValue})}</Text>}
      />
    );
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
    if (remoteSearch) {
      return;
    }

    if (!e.currentTarget.value) {
      setFilteredOptions(
        availableWorkstreamOptions
          ?.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
          .slice(0, 10)
      );
    } else {
      setFilteredOptions(
        availableWorkstreamOptions
          ?.filter((workstream) =>
            workstream.name.toLowerCase().includes(e.currentTarget.value.toLowerCase())
          )
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
          .slice(0, 10)
      );
    }
  };

  const handleKeyDown: React.KeyboardEventHandler = (e) => {
    e.stopPropagation();
  };

  return (
    <Typeahead.Wrapper onSelection={handleSelection}>
      {({getTriggerProps, getContentProps}) => (
        <>
          <TextInput.Container ref={inputContainerRef} size="large" style={{alignItems: 'center'}}>
            <TextInput.Accessory position="start">
              {inputIcon ? (
                inputIcon
              ) : (
                <UIIcon src={SearchLine} style={{display: 'block', paddingTop: 8}} />
              )}
            </TextInput.Accessory>
            <TextInput.Input
              disabled={disabled}
              autoFocus={autoFocus}
              placeholder={placeholder || t('select_workstream')}
              value={inputValue || valueFallback || ''}
              {...getTriggerProps({
                onChange: onInputChange,
                onKeyDown: handleKeyDown,
              })}
            />
          </TextInput.Container>
          <Typeahead.Container
            key={resultsSignature}
            {...getContentProps()}
            triggerRef={inputContainerRef}
            style={menuStyle}
          >
            {isSearching ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{height: '100px'}}
              >
                <Spinner size="small" />
              </Box>
            ) : results.length ? (
              <Typeahead.Results
                results={results}
                renderRow={renderTypeaheadRow}
                initialResults={initialResults}
                // Without `maxResults`, DIG will trim the results to 8 by default.
                maxResults={results.length}
              />
            ) : null}
          </Typeahead.Container>
        </>
      )}
    </Typeahead.Wrapper>
  );
};
